import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Clapperboard,
  Heart,
  ImagePlus,
  Loader2,
  MapPin,
  MessageCircle,
  Send,
  Users,
  X,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { CITIES } from "@/constants/markets";

const LIKED_KEY = "rlr-liked-posts";

const loadLiked = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(LIKED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

const saveLiked = (set) => {
  try {
    localStorage.setItem(LIKED_KEY, JSON.stringify([...set]));
  } catch {
    // ignore
  }
};

const timeAgo = (iso, lang) => {
  try {
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
    const diff = (new Date(iso).getTime() - Date.now()) / 1000;
    const units = [
      ["year", 31536000],
      ["month", 2592000],
      ["week", 604800],
      ["day", 86400],
      ["hour", 3600],
      ["minute", 60],
    ];
    for (const [unit, secs] of units) {
      if (Math.abs(diff) >= secs) return rtf.format(Math.round(diff / secs), unit);
    }
    return rtf.format(Math.round(diff), "second");
  } catch {
    return "";
  }
};

const initials = (name) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ name, role }) => {
  const isAdmin = role === "League Admin";
  return (
    <div
      className={
        isAdmin
          ? "h-10 w-10 rounded-full bg-[hsl(var(--news-red))] text-white flex items-center justify-center font-display text-sm shrink-0"
          : "h-10 w-10 rounded-full bg-[hsl(var(--navy))] text-white flex items-center justify-center font-display text-sm shrink-0"
      }
    >
      {initials(name || "?")}
    </div>
  );
};

const RoleBadge = ({ role }) => {
  const { t } = useLang();
  const map = {
    Captain: { label: t("roleCaptain"), cls: "bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]" },
    Player: { label: t("rolePlayer"), cls: "bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]" },
    "League Admin": { label: t("roleAdmin"), cls: "bg-[hsl(var(--news-red))] text-white" },
  };
  const conf = map[role] || { label: role, cls: "bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))]" };
  return <Badge className={`${conf.cls} hover:${conf.cls} text-[10px] px-1.5 py-0`}>{conf.label}</Badge>;
};

const ComposeBox = ({ city, onCreatePost }) => {
  const { t } = useLang();
  const [text, setText] = useState("");
  const [attached, setAttached] = useState(false);
  const [postCity, setPostCity] = useState(city !== "all" ? city : "");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setPostCity(city !== "all" ? city : "");
  }, [city]);

  const canPost = text.trim() && (city !== "all" || postCity);

  const handlePost = async () => {
    if (!canPost || submitting) return;
    setSubmitting(true);
    try {
      await onCreatePost({
        city: city !== "all" ? city : postCity,
        text: text.trim(),
        hasImage: attached,
      });
      setText("");
      setAttached(false);
    } catch (e) {
      console.error("Failed to create post", e);
      toast.error(t("toastPostError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card
      data-testid="compose-post-card"
      className="bg-[hsl(var(--card))] border-[hsl(var(--border))] shadow-sm mb-6"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex gap-3">
          <Avatar name={t("you")} role="Captain" />
          <div className="flex-1 min-w-0">
            <Textarea
              data-testid="compose-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("whatsHappening")}
              maxLength={500}
              rows={2}
              className="bg-[hsl(var(--surface-2))] border-transparent focus-visible:ring-2 focus-visible:ring-[hsl(var(--news-red))] resize-none text-sm sm:text-base"
            />
            {attached && (
              <div
                data-testid="compose-attached-chip"
                className="mt-2 inline-flex items-center gap-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] text-xs font-semibold px-3 py-1.5 rounded-full"
              >
                <Clapperboard className="h-3.5 w-3.5" />
                {t("mediaAttached")}
                <button
                  type="button"
                  onClick={() => setAttached(false)}
                  className="hover:text-[hsl(var(--news-red))] transition-colors"
                  aria-label={t("removeFile")}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <Button
                type="button"
                data-testid="compose-attach-button"
                variant="outline"
                size="sm"
                onClick={() => setAttached((a) => !a)}
                className="h-9 border-[hsl(var(--border))] text-[hsl(var(--navy))] hover:bg-[hsl(var(--accent))]"
              >
                <ImagePlus className="h-4 w-4 me-2" />
                {t("attachMedia")}
              </Button>
              {city === "all" && (
                <Select value={postCity} onValueChange={setPostCity}>
                  <SelectTrigger
                    data-testid="compose-city-select"
                    className="h-9 w-[160px] bg-[hsl(var(--surface))] border-[hsl(var(--input))] text-sm focus:ring-2 focus:ring-[hsl(var(--news-red))]"
                  >
                    <SelectValue placeholder={t("selectCity")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--popover))]">
                    {CITIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                type="button"
                data-testid="compose-submit-button"
                onClick={handlePost}
                disabled={!canPost || submitting}
                className="ms-auto bg-[hsl(var(--news-red))] text-white hover:bg-[hsl(var(--news-red-dark))] active:scale-[0.98] transition-colors h-9 px-5 font-semibold"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 me-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 me-2" />
                )}
                {submitting ? t("posting") : t("submitPost")}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const PostCard = ({ post, liked, onToggleLike, onAddComment }) => {
  const { t, lang } = useLang();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [sending, setSending] = useState(false);

  const displayAuthor = post.author === "You" ? t("you") : post.author;

  const submitComment = async () => {
    const text = commentText.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await onAddComment(post.id, text);
      setCommentText("");
    } catch (e) {
      console.error("Failed to add comment", e);
      toast.error(t("toastPostError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Card
      data-testid="feed-post-card"
      className="bg-[hsl(var(--card))] border-[hsl(var(--border))] shadow-sm hover:shadow-md transition-shadow"
    >
      <CardContent className="p-4 sm:p-5">
        {/* Post header */}
        <div className="flex items-center gap-3">
          <Avatar name={post.author} role={post.role} />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-sm text-[hsl(var(--ink))]">
                {displayAuthor}
              </span>
              <RoleBadge role={post.role} />
            </div>
            <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              <span data-testid="post-timestamp">{timeAgo(post.createdAt, lang)}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-0.5">
                <MapPin className="h-3 w-3" />
                {post.city}
              </span>
            </div>
          </div>
        </div>

        {/* Post content */}
        <p className="mt-3 text-sm sm:text-base text-[hsl(var(--ink))] leading-relaxed whitespace-pre-wrap">
          {post.text}
        </p>

        {post.hasImage && (
          <div
            data-testid="post-media-placeholder"
            className="mt-3 rounded-xl bg-[hsl(var(--navy))] aspect-video flex flex-col items-center justify-center gap-2 text-white/80"
          >
            <Clapperboard className="h-10 w-10" />
            <span className="text-xs uppercase tracking-[0.18em] font-display">
              {t("highlightClip")}
            </span>
          </div>
        )}

        {/* Action bar */}
        <div className="flex items-center gap-5 mt-4 pt-3 border-t border-[hsl(var(--border))]">
          <button
            type="button"
            data-testid="like-button"
            onClick={() => onToggleLike(post.id, !liked)}
            className={
              liked
                ? "inline-flex items-center gap-1.5 text-[hsl(var(--news-red))] font-semibold text-sm transition-colors"
                : "inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--news-red))] font-medium text-sm transition-colors"
            }
            aria-pressed={liked}
          >
            <Heart className={liked ? "h-4.5 w-4.5 h-5 w-5 fill-current" : "h-5 w-5"} />
            <span data-testid="like-count" className="tabular-nums">
              {post.likes}
            </span>
          </button>
          <button
            type="button"
            data-testid="comment-button"
            onClick={() => setShowComments((s) => !s)}
            className="inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--navy))] font-medium text-sm transition-colors"
          >
            <MessageCircle className="h-5 w-5" />
            <span data-testid="comment-count" className="tabular-nums">
              {post.comments?.length ?? 0}
            </span>
          </button>
        </div>

        {/* Comments */}
        {showComments && (
          <div data-testid="comment-section" className="mt-3 space-y-2.5">
            {(post.comments || []).map((c) => (
              <div
                key={c.id}
                data-testid="comment-item"
                className="bg-[hsl(var(--surface-2))] rounded-lg px-3 py-2"
              >
                <span className="text-xs font-bold text-[hsl(var(--navy))] me-2">
                  {c.author === "You" ? t("you") : c.author}
                </span>
                <span className="text-sm text-[hsl(var(--ink))]">{c.text}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-1">
              <Input
                data-testid="comment-input"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    submitComment();
                  }
                }}
                placeholder={t("writeComment")}
                maxLength={280}
                className="h-9 bg-[hsl(var(--surface-2))] border-transparent focus-visible:ring-2 focus-visible:ring-[hsl(var(--news-red))] text-sm"
              />
              <Button
                type="button"
                data-testid="comment-submit-button"
                size="sm"
                onClick={submitComment}
                disabled={!commentText.trim() || sending}
                className="h-9 bg-[hsl(var(--navy))] text-white hover:bg-[hsl(var(--navy-2))] px-3 shrink-0"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                <span className="ms-1.5 hidden sm:inline">{t("send")}</span>
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const Clubhouse = ({ posts, loading, city, onCreatePost, onToggleLike, onAddComment }) => {
  const { t } = useLang();
  const [likedIds, setLikedIds] = useState(loadLiked);

  const handleToggleLike = (postId, like) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (like) next.add(postId);
      else next.delete(postId);
      saveLiked(next);
      return next;
    });
    onToggleLike(postId, like);
  };

  const sorted = useMemo(
    () => [...posts].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)),
    [posts]
  );

  return (
    <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div className="flex items-center gap-3 mb-1">
        <span className="h-[3px] w-8 bg-[hsl(var(--news-red))] rounded-full" />
        <span className="font-display uppercase tracking-[0.18em] text-xs text-[hsl(var(--news-red))]">
          {t("communityBoard")}
        </span>
      </div>
      <div className="flex items-center gap-2 mb-1">
        <Users className="h-5 w-5 text-[hsl(var(--navy))]" />
        <h2 className="text-lg sm:text-xl font-semibold text-[hsl(var(--ink))]">
          {t("clubhouse")}
          {city !== "all" && (
            <span className="text-[hsl(var(--news-red))]"> · {city}</span>
          )}
        </h2>
        <span className="text-sm text-[hsl(var(--muted-foreground))] tabular-nums">
          ({loading ? "…" : sorted.length})
        </span>
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5">{t("clubhouseDesc")}</p>

      <ComposeBox city={city} onCreatePost={onCreatePost} />

      {loading ? (
        <div data-testid="clubhouse-loading" className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="bg-[hsl(var(--card))]">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <Card data-testid="clubhouse-empty" className="bg-[hsl(var(--surface))]">
          <CardContent className="py-10 text-center space-y-2">
            <p className="font-display uppercase tracking-wide text-2xl text-[hsl(var(--muted-foreground))]">
              {t("clubhouse")}
            </p>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("noPosts")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <AnimatePresence mode="popLayout">
            {sorted.map((post) => (
              <motion.div
                key={post.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <PostCard
                  post={post}
                  liked={likedIds.has(post.id)}
                  onToggleLike={handleToggleLike}
                  onAddComment={onAddComment}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </main>
  );
};
